'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import Link from 'next/link';

// Assets
import Century21 from '@public/png/i-century-21-indonesia-gold.png';
import IBeranda from '@public/svg/i-beranda';
import ICarret from '@public/svg/i-carret';
import IProperti from '@public/svg/i-properti';
import IC21 from '@public/svg/i-c21';
import IInfo from '@public/svg/i-info';
import IContact from '@public/svg/i-contact';
import IArrowRight from '@public/svg/i-arrow-right';

// Hooks
import useRedirect from '@src/hooks/useRedirect';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';

const TABS = [
  {
    label: 'Beranda',
    pathname: '/',
    icon: <IBeranda />,
  },
  {
    label: 'Properti',
    pathname: '/list-listing',
    icon: <IProperti />,
    options: [
      {
        label: 'Beli',
        pathname: '/list-listing/jual',
      },
      {
        label: 'Sewa',
        pathname: '/list-listing/sewa',
      },
    ],
  },
  {
    label: 'Info Franchise',
    pathname: '/franchise',
    icon: <IC21 fill="#FAFAFA" />,
  },
  {
    label: 'Stories',
    pathname: '/stories',
    icon: <IC21 fill="#FAFAFA" />,
  },
  {
    label: 'Tentang',
    pathname: '/about-us',
    icon: <IInfo fill="#FAFAFA" />,
  },
  {
    label: 'Kontak',
    pathname: '/contact',
    icon: <IContact fill="#FAFAFA" />,
  },
];

const RenderOptionLink = ({ option }) => {
  return (
    <Link
      href={option.pathname}
      className={`h-[48px] px-[12px] py-0 text-[#FAFAFA] font-satoshi cursor-pointer text-[14px] flex items-center border-b border-dashed border-[0.5px] border-[#686A68] hover:bg-[#252526] first:rounded-t-xs last:rounded-b-xs last:border-b-0`}
    >
      {option.label}
    </Link>
  );
};

const Tab = ({ label, pathname, currentPath, options, icon }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const isSelected = pathname === `/${currentPath}`;
  const selectedClass = isSelected
    ? 'font-satoshi underline underline-offset-[28px] decoration-2 decoration-[#BEAF87] text-[#BEAF87]'
    : '';

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (options?.length) {
    return (
      <ClickAwayListener onClickAway={handleClose}>
        <div>
          <div
            className={`cursor-pointer text-[14px] flex gap-2 items-center font-satoshi text-[#FAFAFA] h-[72px] ${selectedClass}`}
            onClick={handleClick}
          >
            <div className="flex items-center gap-1">
              {isSelected && icon && icon} {label}
            </div>
            <div
              className={`w-[12px] h-[12px] flex items-center justify-center aspect-square object-contain transition-transform ${
                open ? 'rotate-180' : ''
              }`}
            >
              <ICarret color="#FAFAFA" />
            </div>
          </div>

          <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            style={{ zIndex: 1000 }}
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, 8],
                },
              },
            ]}
          >
            <div className="bg-[#141415] rounded-xs w-[159px]" style={{ boxShadow: '0 0 8px 0 rgba(38, 38, 39, 0.08)' }}>
              {options.map((option, i) => (
                <div key={`navbar-option-${i}`} onClick={handleClose}>
                  <RenderOptionLink option={option} />
                </div>
              ))}
            </div>
          </Popper>
        </div>
      </ClickAwayListener>
    );
  }
  return (
    <Link
      href={pathname}
      className={`h-[72px] cursor-pointer text-[14px] flex gap-1 items-center font-satoshi text-[#FAFAFA] ${selectedClass}`}
    >
      {label}
    </Link>
  );
};

const Navbar = ({ className }) => {
  const redirect = useRedirect();
  const currentPath = usePathname();
  const handleToHomepage = () => {
    redirect('/');
  };

  return (
    <div
      className={`section-container relative bg-[#252526] border-b-[0.5px] border-solid border-[#E6E7E8] ${className} dd2-xl:hidden`}
    >
      <div
        className="w-full flex items-center justify-between max-w-[1344px] 
       dd2-2xl:px-[24px] dd2-2xl:max-w-[unset]
       dd2-xl:px-6"
      >
        <div
          className="cursor-pointer flex flex-col"
          onClick={handleToHomepage}
        >
          <Image
            src={Century21}
            alt="CENTURY 21 Indonesia"
            width={137}
            height={32}
            className="cursor-pointer object-contain"
          />
        </div>
        <div className="flex items-center gap-4">
          {TABS.map((t, i) => (
            <Tab
              key={`century-21-tabs-${i}`}
              {...t}
              currentPath={currentPath.split('/')[1]}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Navbar;
